import { getGrowthSiteContext } from "./growthAnalytics";

const ATTRIBUTION_KEY_PREFIX = "asanmod_growth_attribution_v2";
const PENDING_SIGN_UP_KEY_PREFIX = "asanmod_growth_pending_signup_v2";
const PENDING_SIGN_UP_TTL_MS = 15 * 60 * 1000;
const SAFE_ATTRIBUTION_VALUE = /^[\p{L}\p{N}._~:/+-]{1,100}$/u;

export type GrowthAttribution = {
  firstTouchPath: string;
  lastTouchPath: string;
  toolSlug?: string;
  toolSourcePath?: string;
  contentType?: "blog" | "money_page";
  contentSlug?: string;
  contentSourcePath?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
};

type PendingSignUp = {
  conversionEventId: string;
  method: "google";
  createdAt: number;
};

function sessionStore(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function scopedKey(prefix: string): string | null {
  const context = getGrowthSiteContext();
  return context ? `${prefix}:${context.brand}:${context.site_host}` : null;
}

function readJson<Value>(key: string | null): Value | null {
  if (!key) return null;
  const raw = sessionStore()?.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Value;
  } catch {
    sessionStore()?.removeItem(key);
    return null;
  }
}

function writeJson(key: string | null, value: unknown): void {
  if (!key) return;
  sessionStore()?.setItem(key, JSON.stringify(value));
}

function safePath(pathname: string): string {
  const path = String(pathname || "").trim();
  return path.startsWith("/") && !/[?#\r\n]/.test(path) && path.length <= 256
    ? path
    : "/";
}

function safeAttributionValue(value: string | null): string | undefined {
  const normalized = value?.trim();
  return normalized && SAFE_ATTRIBUTION_VALUE.test(normalized)
    ? normalized
    : undefined;
}

function utmParams(search: string): Partial<GrowthAttribution> {
  const params = new URLSearchParams(search);
  return {
    utmSource: safeAttributionValue(params.get("utm_source")),
    utmMedium: safeAttributionValue(params.get("utm_medium")),
    utmCampaign: safeAttributionValue(params.get("utm_campaign")),
    utmTerm: safeAttributionValue(params.get("utm_term")),
    utmContent: safeAttributionValue(params.get("utm_content")),
  };
}

export function captureGrowthAttribution(
  pathname: string,
  search = ""
): GrowthAttribution {
  const attributionKey = scopedKey(ATTRIBUTION_KEY_PREFIX);
  const normalizedPath = safePath(pathname);
  const existing = readJson<GrowthAttribution>(attributionKey);
  const currentUtm = utmParams(search);
  const next: GrowthAttribution = {
    firstTouchPath: existing?.firstTouchPath || normalizedPath,
    lastTouchPath: normalizedPath,
    toolSlug: existing?.toolSlug,
    toolSourcePath: existing?.toolSourcePath,
    contentType: existing?.contentType,
    contentSlug: existing?.contentSlug,
    contentSourcePath: existing?.contentSourcePath,
    utmSource: existing?.utmSource || currentUtm.utmSource,
    utmMedium: existing?.utmMedium || currentUtm.utmMedium,
    utmCampaign: existing?.utmCampaign || currentUtm.utmCampaign,
    utmTerm: existing?.utmTerm || currentUtm.utmTerm,
    utmContent: existing?.utmContent || currentUtm.utmContent,
  };
  writeJson(attributionKey, next);
  return next;
}

export function recordToolAttribution(
  pathname: string,
  toolSlug: string
): GrowthAttribution {
  const normalizedPath = safePath(pathname);
  const current = captureGrowthAttribution(normalizedPath);
  const next = {
    ...current,
    lastTouchPath: normalizedPath,
    toolSlug,
    toolSourcePath: normalizedPath,
    utmContent: current.utmContent || `tool:${toolSlug}`,
  };
  writeJson(scopedKey(ATTRIBUTION_KEY_PREFIX), next);
  return next;
}

export function recordContentAttribution(
  pathname: string,
  contentType: "blog" | "money_page",
  contentSlug: string
): GrowthAttribution {
  const normalizedPath = safePath(pathname);
  const current = captureGrowthAttribution(normalizedPath);
  const next = {
    ...current,
    lastTouchPath: normalizedPath,
    contentType,
    contentSlug,
    contentSourcePath: normalizedPath,
  };
  writeJson(scopedKey(ATTRIBUTION_KEY_PREFIX), next);
  return next;
}

function getGrowthAttribution(fallbackPath: string): GrowthAttribution {
  return (
    readJson<GrowthAttribution>(scopedKey(ATTRIBUTION_KEY_PREFIX)) || {
      firstTouchPath: fallbackPath,
      lastTouchPath: fallbackPath,
    }
  );
}

export function growthEventAttribution(fallbackPath: string) {
  const value = getGrowthAttribution(fallbackPath);
  return {
    source_path: value.toolSourcePath || value.firstTouchPath || fallbackPath,
    tool_slug: value.toolSlug,
    content_type: value.contentType,
    content_slug: value.contentSlug,
    utm_source: value.utmSource,
    utm_medium: value.utmMedium,
    utm_campaign: value.utmCampaign,
    utm_content: value.utmContent,
  };
}

export function leadRequestAttribution(
  fallbackPath: string,
  currentUtm: Partial<GrowthAttribution> = {}
) {
  const value = getGrowthAttribution(fallbackPath);
  return {
    sourcePath: value.toolSourcePath || value.contentSourcePath || fallbackPath,
    utmSource: currentUtm.utmSource || value.utmSource,
    utmMedium: currentUtm.utmMedium || value.utmMedium,
    utmCampaign: currentUtm.utmCampaign || value.utmCampaign,
    utmTerm: currentUtm.utmTerm || value.utmTerm,
    utmContent:
      currentUtm.utmContent ||
      value.utmContent ||
      (value.toolSlug
        ? `tool:${value.toolSlug}`
        : value.contentSlug
          ? `content:${value.contentSlug}`
          : undefined),
  };
}

export function markPendingGoogleSignUp(conversionEventId: string): void {
  writeJson(scopedKey(PENDING_SIGN_UP_KEY_PREFIX), {
    conversionEventId,
    method: "google",
    createdAt: Date.now(),
  } satisfies PendingSignUp);
}

export function consumePendingGoogleSignUp(
  pathname: string,
  search: string
): PendingSignUp | null {
  if (pathname !== "/dashboard") return null;
  const pendingSignUpKey = scopedKey(PENDING_SIGN_UP_KEY_PREFIX);
  const pending = readJson<PendingSignUp>(pendingSignUpKey);
  if (!pending || Date.now() - pending.createdAt > PENDING_SIGN_UP_TTL_MS) {
    if (pendingSignUpKey) sessionStore()?.removeItem(pendingSignUpKey);
    return null;
  }
  const params = new URLSearchParams(search);
  if (
    params.get("googleSignUp") !== "1" ||
    params.get("conversionEventId") !== pending.conversionEventId
  ) {
    return null;
  }
  if (pendingSignUpKey) sessionStore()?.removeItem(pendingSignUpKey);
  return pending;
}
